import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, normalizeClass as _normalizeClass, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, Fragment as _Fragment, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, Transition as _Transition, withCtx as _withCtx, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "header-left" }
const _hoisted_2 = { class: "header-title" }
const _hoisted_3 = { class: "header-right" }
const _hoisted_4 = {
  class: "drag-handle",
  title: "Drag to move"
}
const _hoisted_5 = { class: "caller-info-section" }
const _hoisted_6 = { class: "avatar-container" }
const _hoisted_7 = { class: "avatar-circle" }
const _hoisted_8 = { class: "avatar-initials" }
const _hoisted_9 = {
  key: 0,
  class: "caller-name"
}
const _hoisted_10 = { class: "caller-phone" }
const _hoisted_11 = {
  key: 1,
  class: "caller-company"
}
const _hoisted_12 = {
  key: 2,
  class: "call-status"
}
const _hoisted_13 = {
  key: 3,
  class: "call-timer"
}
const _hoisted_14 = {
  key: 0,
  class: "transcription-section"
}
const _hoisted_15 = { class: "call-actions" }
const _hoisted_16 = { class: "action-group" }
const _hoisted_17 = { class: "action-group" }
const _hoisted_18 = {
  key: 1,
  class: "action-group hangup-group"
}

import { computed, ref, watch, onBeforeUnmount, onMounted } from "vue";
import { Icon } from "@iconify/vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { CONTACT_OVERVIEW_ROUTE } from "@/router/routes";
import LiveCaption from "@/components/call/LiveCaption.vue";

interface Props {
  show: boolean;
  callerName?: string;
  callerPhoneNumber?: string;
  callerCompany?: string;
  callSid?: string;
  contactId?: string;
  callState?: "ringing" | "active" | null;
  callDuration?: number;
  isMinimized?: boolean;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'IncomingCallModal',
  props: {
    show: { type: Boolean, default: false },
    callerName: { default: "" },
    callerPhoneNumber: { default: "" },
    callerCompany: { default: "" },
    callSid: { default: "" },
    contactId: { default: "" },
    callState: { default: null },
    callDuration: { default: 0 },
    isMinimized: { type: Boolean, default: false }
  },
  emits: ["answer", "decline", "hangup", "close", "minimize"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const emit = __emit;

const router = useRouter();
const store = useStore();

// Transcription state from account settings
const account = computed(() => store.getters.account.data);
const enableTranscription = computed(() => account.value?.enableTranscription !== false);
const enableAISummary = computed(
  () => enableTranscription.value && account.value?.enableAISummary !== false
);
const liveCaptionRef = ref<InstanceType<typeof LiveCaption> | null>(null);

// Expose transcription messages for minimized view
const getTranscriptionMessages = () => {
  return liveCaptionRef.value?.displayedMessages || [];
};

__expose({
  getTranscriptionMessages,
  enableTranscription,
  enableAISummary,
});

const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });
const modalPosition = ref({ x: 0, y: 0 });
const isInitialPosition = ref(true);
const modalRef = ref<HTMLElement | null>(null);

const audioRef = ref<HTMLAudioElement | null>(null);

const modalStyle = computed(() => {
  if (isInitialPosition.value) return {};

  return {
    position: "fixed" as const,
    left: `${modalPosition.value.x}px`,
    top: `${modalPosition.value.y}px`,
    transform: "none",
    margin: "0",
    width: "360px",
    maxWidth: "450px",
    borderRadius: "16px !important",
    zIndex: "10000",
  };
});

const callerInitials = computed(() => {
  if (props.callerName) {
    const names = props.callerName.trim().split(/\s+/);
    if (names.length >= 2) {
      return (names[0][0] + names[names.length - 1][0]).toUpperCase();
    }
    return names[0][0].toUpperCase();
  }
  if (props.callerPhoneNumber) {
    const digits = props.callerPhoneNumber.replace(/\D/g, "");
    return digits.slice(-2) || "??";
  }
  return "??";
});

const formattedPhoneNumber = computed(() => {
  if (!props.callerPhoneNumber) return "Unknown Number";
  const cleaned = props.callerPhoneNumber.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  return props.callerPhoneNumber;
});

const startDrag = (event: MouseEvent | TouchEvent) => {
  const target = event.target as HTMLElement;
  const excludedSelectors = [
    ".btn-close",
    ".btn-minimize",
    ".action-btn",
    ".action-label",
    ".call-actions",
    ".caller-info-section",
    ".transcription-section",
    ".transcription-toggle",
    ".toggle-item",
  ];

  if (excludedSelectors.some((selector) => target.closest(selector))) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  isDragging.value = true;

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  if (isInitialPosition.value) {
    const modal = modalRef.value;
    if (modal) {
      const rect = modal.getBoundingClientRect();
      modalPosition.value = { x: rect.left, y: rect.top };
    } else {
      modalPosition.value = {
        x: (window.innerWidth - 360) / 2,
        y: (window.innerHeight - 400) / 2,
      };
    }
    isInitialPosition.value = false;
  }

  dragStart.value = {
    x: clientX - modalPosition.value.x,
    y: clientY - modalPosition.value.y,
  };

  const dragEvents = [
    ["mousemove", handleDrag, { passive: false }],
    ["mouseup", stopDrag],
    ["touchmove", handleDrag, { passive: false }],
    ["touchend", stopDrag],
  ] as const;

  dragEvents.forEach(([event, handler, options]) => {
    document.addEventListener(event, handler as any, options);
  });
};

const handleDrag = (event: MouseEvent | TouchEvent) => {
  if (!isDragging.value) return;

  event.preventDefault();
  event.stopPropagation();

  const clientX = "touches" in event ? event.touches[0].clientX : event.clientX;
  const clientY = "touches" in event ? event.touches[0].clientY : event.clientY;

  let newX = clientX - dragStart.value.x;
  let newY = clientY - dragStart.value.y;

  const modal = modalRef.value;
  if (modal) {
    const rect = modal.getBoundingClientRect();
    const padding = 10;
    newX = Math.max(padding, Math.min(newX, window.innerWidth - rect.width - padding));
    newY = Math.max(padding, Math.min(newY, window.innerHeight - rect.height - padding));
  }

  modalPosition.value = { x: newX, y: newY };
};

const stopDrag = () => {
  isDragging.value = false;

  const dragEvents = ["mousemove", "mouseup", "touchmove", "touchend"];
  const handlers = [handleDrag, stopDrag, handleDrag, stopDrag];

  dragEvents.forEach((event, index) => {
    document.removeEventListener(event, handlers[index] as any);
  });
};

// Audio functions
const RINGING_SOUND_URL = new URL("../../assets/audio/ringing-new.m4a", import.meta.url).href;

const initAudio = () => {
  if (audioRef.value) return;

  try {
    audioRef.value = new Audio(RINGING_SOUND_URL);
    audioRef.value.loop = true;
    audioRef.value.volume = 0.7;
  } catch (error) {
    console.error("Failed to initialize audio:", error);
  }
};

const playRingingSound = async () => {
  if (!audioRef.value) {
    initAudio();
  }

  if (audioRef.value) {
    try {
      await audioRef.value.play();
    } catch (error) {
      console.warn("Audio playback failed (may require user interaction):", error);
    }
  }
};

const stopRingingSound = () => {
  if (audioRef.value) {
    audioRef.value.pause();
    audioRef.value.currentTime = 0;
  }
};

const formattedCallDuration = computed(() => {
  if (props.callDuration > 0) {
    const hours = Math.floor(props.callDuration / 3600);
    const minutes = Math.floor((props.callDuration % 3600) / 60);
    const seconds = props.callDuration % 60;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return "00:00:00";
});

const handleAnswer = () => {
  stopRingingSound();
  emit("answer");
};

const handleHangup = () => {
  stopRingingSound();
  resetPosition();
  emit("hangup");
};

const handleDecline = () => {
  stopRingingSound();
  resetPosition();
  emit("decline");
  emit("close");
};

const handleClose = () => {
  stopRingingSound();
  resetPosition();
  emit("close");
};

const handleMinimize = () => {
  emit("minimize");
};

const handleViewContact = () => {
  if (props.contactId) {
    router.push({
      name: CONTACT_OVERVIEW_ROUTE,
      params: { id: props.contactId },
    });
  }
};

const resetPosition = () => {
  isInitialPosition.value = true;
  modalPosition.value = { x: 0, y: 0 };
};

const handleKeydown = (event: KeyboardEvent) => {
  if (!props.show) return;

  if ((event.key === "Enter" || event.key === " ") && props.callState === "ringing") {
    event.preventDefault();
    handleAnswer();
  } else if (event.key === "Escape" && props.callState === "ringing") {
    event.preventDefault();
    handleDecline();
  }
};

watch(
  () => props.show,
  (newValue) => {
    if (newValue) {
      playRingingSound();
      document.addEventListener("keydown", handleKeydown);
    } else {
      stopRingingSound();
      document.removeEventListener("keydown", handleKeydown);
      resetPosition();
    }
  },
  { immediate: true }
);

onBeforeUnmount(() => {
  stopRingingSound();
  document.removeEventListener("mousemove", handleDrag);
  document.removeEventListener("mouseup", stopDrag);
  document.removeEventListener("touchmove", handleDrag);
  document.removeEventListener("touchend", stopDrag);
  document.removeEventListener("keydown", handleKeydown);

  if (audioRef.value) {
    audioRef.value = null;
  }
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
    _createVNode(_Transition, { name: "modal-fade" }, {
      default: _withCtx(() => [
        (__props.show && !__props.isMinimized)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: _normalizeClass(["incoming-call-overlay", { 'dragging-active': !isInitialPosition.value }])
            }, [
              _createElementVNode("div", {
                ref_key: "modalRef",
                ref: modalRef,
                class: _normalizeClass(["incoming-call-modal", { 'is-dragging': isDragging.value }]),
                style: _normalizeStyle(modalStyle.value),
                onClick: _cache[0] || (_cache[0] = _withModifiers(() => {}, ["stop"])),
                onMousedown: startDrag,
                onTouchstart: startDrag
              }, [
                _createElementVNode("div", {
                  class: _normalizeClass(["call-header", { draggable: true }])
                }, [
                  _createElementVNode("div", _hoisted_1, [
                    _createVNode(_unref(Icon), {
                      icon: "mdi:phone",
                      class: "phone-icon"
                    }),
                    _cache[1] || (_cache[1] = _createElementVNode("span", { class: "status-dot" }, null, -1)),
                    _createElementVNode("h6", _hoisted_2, _toDisplayString(__props.callState === "active" ? "Active Call" : "Incoming Call"), 1)
                  ]),
                  _createElementVNode("div", _hoisted_3, [
                    _createElementVNode("button", {
                      type: "button",
                      class: "btn-minimize",
                      onClick: handleMinimize,
                      title: "Minimize",
                      "aria-label": "Minimize call"
                    }, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:minus",
                        width: "16",
                        height: "16"
                      })
                    ]),
                    _createElementVNode("div", _hoisted_4, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:drag",
                        width: "16",
                        height: "16"
                      })
                    ]),
                    _createElementVNode("button", {
                      type: "button",
                      class: "btn-close",
                      onClick: handleClose,
                      "aria-label": "Close"
                    }, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:close",
                        width: "18",
                        height: "18"
                      })
                    ])
                  ])
                ]),
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("div", _hoisted_6, [
                    _cache[2] || (_cache[2] = _createElementVNode("div", { class: "pulse-ring pulse-ring-1" }, null, -1)),
                    _cache[3] || (_cache[3] = _createElementVNode("div", { class: "pulse-ring pulse-ring-2" }, null, -1)),
                    _createElementVNode("div", _hoisted_7, [
                      _createElementVNode("span", _hoisted_8, _toDisplayString(callerInitials.value), 1)
                    ])
                  ]),
                  (__props.callerName)
                    ? (_openBlock(), _createElementBlock("h3", _hoisted_9, _toDisplayString(__props.callerName), 1))
                    : _createCommentVNode("", true),
                  _createElementVNode("p", _hoisted_10, _toDisplayString(formattedPhoneNumber.value), 1),
                  (__props.callerCompany)
                    ? (_openBlock(), _createElementBlock("p", _hoisted_11, _toDisplayString(__props.callerCompany), 1))
                    : _createCommentVNode("", true),
                  (__props.callState === 'ringing')
                    ? (_openBlock(), _createElementBlock("p", _hoisted_12, "Ringing..."))
                    : (__props.callState === 'active')
                      ? (_openBlock(), _createElementBlock("p", _hoisted_13, _toDisplayString(formattedCallDuration.value), 1))
                      : _createCommentVNode("", true),
                  (__props.contactId)
                    ? (_openBlock(), _createElementBlock("button", {
                        key: 4,
                        class: "view-contact-btn",
                        onClick: handleViewContact,
                        "aria-label": "View contact"
                      }, [
                        _createVNode(_unref(Icon), {
                          icon: "mdi:account-details-outline",
                          width: "16",
                          height: "16"
                        }),
                        _cache[4] || (_cache[4] = _createTextVNode(" View Contact ", -1))
                      ]))
                    : _createCommentVNode("", true)
                ]),
                (__props.callState === 'active' && enableTranscription.value && __props.callSid)
                  ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                      _createVNode(LiveCaption, {
                        "is-active": __props.callState === 'active',
                        enabled: enableTranscription.value,
                        "call-sid": __props.callSid,
                        ref_key: "liveCaptionRef",
                        ref: liveCaptionRef
                      }, null, 8, ["is-active", "enabled", "call-sid"])
                    ]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_15, [
                  (__props.callState === 'ringing')
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _createElementVNode("div", _hoisted_16, [
                          _createElementVNode("button", {
                            class: "action-btn decline-btn",
                            onClick: handleDecline,
                            "aria-label": "Decline call"
                          }, [
                            _createVNode(_unref(Icon), {
                              icon: "mdi:phone-hangup",
                              width: "20",
                              height: "20"
                            })
                          ]),
                          _cache[5] || (_cache[5] = _createElementVNode("span", { class: "action-label" }, "Decline", -1))
                        ]),
                        _createElementVNode("div", _hoisted_17, [
                          _createElementVNode("button", {
                            class: "action-btn answer-btn",
                            onClick: handleAnswer,
                            "aria-label": "Answer call"
                          }, [
                            _createVNode(_unref(Icon), {
                              icon: "mdi:phone",
                              width: "20",
                              height: "20"
                            })
                          ]),
                          _cache[6] || (_cache[6] = _createElementVNode("span", { class: "action-label" }, "Answer", -1))
                        ])
                      ], 64))
                    : (__props.callState === 'active')
                      ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                          _createElementVNode("button", {
                            class: "action-btn hangup-btn",
                            onClick: handleHangup,
                            "aria-label": "Hangup call"
                          }, [
                            _createVNode(_unref(Icon), {
                              icon: "mdi:phone-hangup",
                              width: "20",
                              height: "20"
                            })
                          ]),
                          _cache[7] || (_cache[7] = _createElementVNode("span", { class: "action-label" }, "Hangup", -1))
                        ]))
                      : _createCommentVNode("", true)
                ])
              ], 38)
            ], 2))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ]))
}
}

})